import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Switch, Group, Text, createStyles } from "@mantine/core";

import PremiumModal from "../../app/PremiumModal";

import SpaceModel from "../../../models/Space";
import SpaceSettingModel from "../../../models/SpaceSetting";

import { setSpace } from "../../../store/app";

const useStyles = createStyles((theme) => ({
  item: {
    paddingBottom: 16,
    marginBottom: 18,
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
  },
  itemLast: {
    paddingBottom: 16,
    marginBottom: 18,
  },
  itemTitle: {
    fontSize: 18,
  },
  itemDescription: {
    fontSize: 14,
    color: theme.colors.gray[6],
  },
  switch: {
    "& *": {
      cursor: "pointer",
    },
  },
}));

export default function PageLayout() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { classes } = useStyles();

  const space = useSelector((state) => state.space);

  const hiddenLayoutElements = useRef([]);
  const loading = useRef(false);
  const showPageIcon = useRef(false);

  const [state, setState] = useState({
    sidebar: false,
    header: false,
    cover: false,
    title: false,
    breadcrumbs: true,
    width: false,
    show_page_icon: false,
  });

  const [opened, setOpened] = useState(false);
  const [validatePremium, setValidatePremium] = useState(false);

  useEffect(() => {
    if (space && space.slug) {
      loading.current = true;

      hiddenLayoutElements.current = window.$hiddenLayoutElements
        ? [...window.$hiddenLayoutElements]
        : [...space.hidden_layout_elements];

      setState({
        sidebar: hiddenLayoutElements.current.includes("sidebar"),
        header: hiddenLayoutElements.current.includes("header"),
        cover: hiddenLayoutElements.current.includes("cover"),
        title: hiddenLayoutElements.current.includes("title"),
        breadcrumbs: hiddenLayoutElements.current.includes("breadcrumbs"),
        width: space.space_width === "wide",
        show_page_icon: space.show_page_icon,
      });

      setValidatePremium(space?.permissions?.can_set_layout || false);

      setTimeout(() => (loading.current = false), 1000);
    }
  }, [space]);

  useEffect(() => {
    if (!loading.current) {
      const auxSpace = { ...space, space_width: state.width ? "wide" : "narrow" };
      dispatch(setSpace(auxSpace));
      window.$spaceWideWidth = state.width;
      onSaveSettings();
      setTimeout(() => window.location.reload(), 500);
    }
  }, [state.width]);

  const toggleSetting = (key) => (checked) => {
    if (!validatePremium[0] && !loading.current) {
      setState((prev) => ({ ...prev, [key]: false }));
      setOpened([true, validatePremium[1], validatePremium[2]]);
      return;
    }

    setState((prev) => ({ ...prev, [key]: checked }));

    if (key === "show_page_icon") {
      showPageIcon.current = checked;
    }

    if (checked && !hiddenLayoutElements.current.includes(key)) {
      hiddenLayoutElements.current.push(key);
    } else if (!checked && hiddenLayoutElements.current.includes(key)) {
      hiddenLayoutElements.current = hiddenLayoutElements.current.filter((e) => e !== key);
    }

    onSave();
  };

  const onSave = () => {
    if (loading.current) return;

    window.$hiddenLayoutElements = [...hiddenLayoutElements.current];
    SpaceModel.onUpdate(
      space.slug,
      {
        hidden_layout_elements: [...hiddenLayoutElements.current],
        space_width: state.width ? "wide" : "narrow",
        show_page_icon: showPageIcon.current
      },
      () => {},
      () => {}
    );
  };

  const onSaveSettings = () => {
    SpaceSettingModel.onUpdate(
      space.slug,
      {
        pod_settings_attributes: {
          space_width: state.width ? "wide" : "narrow",
          show_page_icon: showPageIcon.current
        },
      },
      () => {},
      () => {}
    );
  };

  const settings = [
    {
      key: "width",
      title: t("spaces.colors.pageLayout.widePage"),
      desc: "Content on this page will display wider.",
    },
    {
      key: "sidebar",
      title: t("spaces.colors.pageLayout.hideSidebar"),
      desc: "The sidebar will no longer be accessible on this page.",
    },
    {
      key: "header",
      title: t("spaces.colors.pageLayout.hideHeader"),
      desc: "The header on this page will only be visible to managers.",
    },
    {
      key: "cover",
      title: t("spaces.colors.pageLayout.hideCoverImage"),
      desc: "The cover image will not be visible on this page.",
    },
    {
      key: "title",
      title: t("spaces.colors.pageLayout.hidePageTitle"),
      desc: "The page title will not show up at the top of the page.",
    },
    {
      key: "breadcrumbs",
      title: t("spaces.colors.pageLayout.hideBreadcrumbs"),
      desc: "The breadcrumb navigation links will not be visible at the top of this page.",
    },
    {
      key: "show_page_icon",
      title: "Show space icon on content page",
      desc: "The sidebar icon will be displayed next to the title on this page.",
    },
  ];

  return (
    <>
      {settings.map(({ key, title, desc }, idx) => (
        <Group
          key={key}
          position="apart"
          className={idx === settings.length - 1 ? classes.itemLast : classes.item}
          noWrap
          spacing="xl"
        >
          <div>
            <Text className={classes.itemTitle}>{title}</Text>
            <Text className={classes.itemDescription}>{desc}</Text>
          </div>
          <Switch
            checked={state[key]}
            onChange={(e) => toggleSetting(key)(e.currentTarget.checked)}
            onLabel="ON"
            offLabel="OFF"
            className={classes.switch}
            size="lg"
          />
        </Group>
      ))}
      <PremiumModal opened={opened} setOpened={setOpened} />
    </>
  );
}